import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import useSeo from '../../hooks/useSeo';
import './Landing.css';

/* ── Slide data — full-screen category hero images with bold quotes ── */
const SLIDES = [
  {
    category: 'Dosas',
    quote: 'GOLDEN SOUTH INDIAN DOSAS WITH THE PERFECT CRISP BITE',
    img: '/images/landing/dosa.jpg',
  },
  {
    category: 'Biryanis',
    quote: 'BIRYANIS & PULAOS WITH DEEP AROMA AND ROYAL FLAVOR',
    img: '/images/landing/biryani.jpg',
  },
  {
    category: 'Street Bites',
    quote: 'ICONIC INDIAN STREET BITES WITH CRUNCH, SPICE, AND SOUL',
    img: '/images/landing/streetbites.png',
  },
  {
    category: 'Curries',
    quote: 'HANDCRAFTED CURRIES WITH RICH GRAVIES AND LAYERED SPICES',
    img: '/images/landing/curry.jpg',
  },
  {
    category: 'Appetizers',
    quote: 'BOLD BEGINNINGS THAT BRING THE SIZZLE TO YOUR TABLE',
    img: '/images/landing/appetizers.png',
  },
  {
    category: 'Chaats',
    quote: 'VIBRANT CHAATS WITH THE PERFECT BALANCE OF TANG, SWEETNESS, AND SPICE',
    img: '/images/landing/chaats.png',
  },
  {
    category: 'Desserts',
    quote: 'CLASSIC INDIAN SWEETS THAT FINISH YOUR FEAST ON A HIGH NOTE',
    img: '/images/landing/desserts.png',
  },
];

const AUTO_INTERVAL = 4500;

/* ── Signature dishes for gallery — menuCat must match FoodMenu category names ── */
const DISHES = [
  { name: 'Dosa & Uttappam', desc: 'Paper-thin, golden-crisp crepes fermented to perfection — served with house-ground chutneys & sambar', img: '/images/landing/dosa.jpg', tag: 'Bestseller', menuCat: 'Dosa / Pesarattu / Ragi Dosa' },
  { name: 'Biryanis & Pulaos', desc: 'Fragrant basmati slow-cooked in layers of saffron, whole spices & tender proteins — sealed in dum', img: '/images/landing/biryani.jpg', tag: 'Must Try', menuCat: 'Biryanis & Pulaos' },
  { name: 'Street Bites', desc: 'Crispy samosas, mirchi bajji, punugulu & crunchy pakoras — the bold flavors of Indian street food', img: '/images/landing/streetbites.png', tag: 'Popular', menuCat: 'Street Bites' },
  { name: 'Appetizers', desc: 'Sizzling chicken 65, paneer 65 & chilli chicken — perfect shareable starters', img: '/images/landing/appetizers.png', tag: 'Hot', menuCat: 'Appetizers' },
  { name: 'Curries & Gravies', desc: 'Slow-simmered, spice-layered curries from creamy butter chicken to bold Andhra-style gravies', img: '/images/landing/curry.jpg', tag: 'Signature', menuCat: 'Curries' },
  { name: 'Chaats', desc: 'Tangy, sweet & spicy — pani puri, bhel puri & papdi chaat bursting with flavor', img: '/images/landing/chaats.png', tag: 'Fan Favorite', menuCat: 'Chaats' },
  { name: 'Indo Chinese', desc: 'Fiery manchurian, hakka noodles & chili dishes — Indian wok magic', img: '/images/landing/hero-bg.png', tag: 'Fusion', menuCat: 'Indo Chinese' },
  { name: 'Combos & Specials', desc: 'Curated meal combos with idly, vada & more — unbeatable value for the whole family', img: '/images/landing/combos.png', tag: 'Value', menuCat: 'Combos' },
  { name: 'Desserts & Drinks', desc: 'Warm gulab jamun, creamy kulfi & refreshing lassi — the sweetest way to finish', img: '/images/landing/desserts.png', tag: 'Sweet', menuCat: 'Beverages & Desserts' },
];

/* ══════════════════════════════════════════
   Landing Page
   ══════════════════════════════════════════ */
const Landing = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);
  const sectionsRef = useRef([]);

  useSeo({
    description: 'Authentic South Indian & Indian food in Centerton, Northwest Arkansas — crispy dosas, biryanis, curries, chaats, Indo-Chinese & street bites. Order pickup or delivery, or book catering.',
    path: '/',
  });

  /* Auto-advance slider */
  const nextSlide = useCallback(() => {
    setActiveSlide((prev) => (prev + 1) % SLIDES.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    timerRef.current = setInterval(nextSlide, AUTO_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [isPaused, nextSlide]);

  const goToSlide = (idx) => {
    setActiveSlide(idx);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 8000);
  };

  /* Scroll-reveal observer */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('lp-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    sectionsRef.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addRef = (el) => {
    if (el && !sectionsRef.current.includes(el)) sectionsRef.current.push(el);
  };

  return (
    <div className="lp">

      {/* ═══════ NAV BAR (floating over hero) ═══════ */}
      <nav className="lp-nav">
        <div className="lp-nav__inner">
          <Link to="/" className="lp-nav__brand">
            <svg className="lp-nav__brand-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
            <span className="lp-nav__brand-accent">Desi</span>
            <span className="lp-nav__brand-text">Bites</span>
          </Link>
          <div className="lp-nav__links">
            <Link to="/menu" className="lp-nav__link">Menu</Link>
            <Link to="/Catering" className="lp-nav__link">Catering</Link>
            <a href="tel:+14794129916" className="lp-nav__link">Contact</a>
            {/* Points to DoorDash while native ordering is staff-only; switch
                back to <Link to="/order"> at launch. */}
            <a
              href="https://www.doordash.com/store/desi-bites-centerton-31061357"
              target="_blank"
              rel="noopener noreferrer"
              className="lp-nav__cta"
            >
              Order Online
            </a>
          </div>
        </div>
      </nav>

      {/* ═══════ HERO SLIDER (Dum N Grill inspired) ═══════ */}
      <section className="lp-hero">
        {/* Background image slides */}
        {SLIDES.map((slide, i) => (
          <div
            key={i}
            className={`lp-hero__slide ${i === activeSlide ? 'lp-hero__slide--active' : ''}`}
            style={{ backgroundImage: `url(${slide.img})` }}
          />
        ))}
        <div className="lp-hero__overlay" />

        {/* Centered content */}
        <div className="lp-hero__content">
          <h1 className="lp-hero__quote" key={activeSlide}>
            {SLIDES[activeSlide].quote}
          </h1>

          {/* Category selector pills */}
          <div className="lp-hero__categories">
            {SLIDES.map((slide, i) => (
              <button
                key={i}
                className={`lp-hero__cat ${i === activeSlide ? 'lp-hero__cat--active' : ''}`}
                onClick={() => goToSlide(i)}
              >
                {slide.category}
              </button>
            ))}
          </div>

          {/* CTA buttons — add the "Order Pickup" → /order button back here
              when online ordering opens to the public. */}
          <div className="lp-hero__actions">
            <Link to="/menu" className="lp-hero__btn lp-hero__btn--primary">
              View Full Menu
            </Link>
            <Link to="/Catering" className="lp-hero__btn lp-hero__btn--glass">
              Catering
            </Link>
          </div>
        </div>

        {/* Slide progress indicators */}
        <div className="lp-hero__progress">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              className={`lp-hero__dot ${i === activeSlide ? 'lp-hero__dot--active' : ''}`}
              onClick={() => goToSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
            >
              <span
                className="lp-hero__dot-fill"
                style={i === activeSlide && !isPaused ? { animationDuration: `${AUTO_INTERVAL}ms` } : {}}
              />
            </button>
          ))}
        </div>
      </section>

      {/* ═══════ FOOD TRUCK SECTION ═══════ */}
      <section className="lp-truck lp-reveal" ref={addRef}>
        <div className="lp-truck__inner">
          <div className="lp-truck__image-wrap">
            <img
              src="/images/landing/truck.png"
              alt="Desi Bites food truck — lit up at night with string lights"
              className="lp-truck__image"
              loading="eager"
              decoding="async"
            />
            <div className="lp-truck__image-badge">
              🔥 Where Every Bite Tells a Story
            </div>
          </div>
          <div className="lp-truck__text">
            <span className="lp-section-label">Our Story</span>
            <h2 className="lp-section-title">Authentic South Indian Flavors in Arkansas</h2>
            <p className="lp-truck__desc">
              Desi Bites brings the vibrant, authentic flavors of South Indian cuisine to Northwest Arkansas.
              From perfectly crispy dosas and fluffy idlis to aromatic biryanis and bold curries,
              every dish is crafted with traditional recipes and the freshest ingredients.
            </p>
            <p className="lp-truck__desc">
              Whether you're craving a quick street bite or planning a catering spread for your next event,
              come find us at our Centerton location — your taste buds will thank you!
            </p>
          </div>
        </div>
      </section>

      {/* ═══════ SIGNATURE DISHES ═══════ */}
      <section className="lp-dishes lp-reveal" ref={addRef}>
        <div className="lp-dishes__inner">
          <span className="lp-section-label">Signature Dishes</span>
          <h2 className="lp-section-title">Flavors That Tell a Story</h2>
          <p className="lp-section-subtitle">
            Handcrafted South Indian specialties — authentic recipes, freshest ingredients.
          </p>
          <div className="lp-dishes__grid">
            {DISHES.map((dish, i) => (
              <Link to={`/menu?cat=${encodeURIComponent(dish.menuCat)}`} key={i} className="lp-dish">
                <div className="lp-dish__image-wrap">
                  <img src={dish.img} alt={dish.name} className="lp-dish__image" loading="lazy" decoding="async" />
                  <div className="lp-dish__image-overlay" />
                  {dish.tag && <span className="lp-dish__tag">{dish.tag}</span>}
                </div>
                <div className="lp-dish__info">
                  <h3 className="lp-dish__name">{dish.name}</h3>
                  <p className="lp-dish__desc">{dish.desc}</p>
                </div>
              </Link>
            ))}
          </div>
          <Link to="/menu" className="lp-dishes__cta">
            View Full Menu →
          </Link>
        </div>
      </section>

      {/* ═══════ GAME PROMO ═══════ */}
      <section className="lp-game lp-reveal" ref={addRef}>
        <div className="lp-game__inner">
          <div className="lp-game__left">
            <div className="lp-game__emoji-row">
              {'🍛🥘🥟🌶️⭐💎🪰'.split('').filter(Boolean).map((e, i) => (
                <span key={i} className="lp-game__float-emoji" style={{ animationDelay: `${i * 0.2}s` }}>{e}</span>
              ))}
            </div>
            <h2 className="lp-game__title">Play <span>Desi Bites Dash!</span></h2>
            <p className="lp-game__desc">
              Think you can handle the kitchen heat? Catch falling Indian food, dodge the bugs, and see how high you can score. Free to play — no sign-up needed!
            </p>
            <div className="lp-game__badges">
              <span>🎮 Arcade</span>
              <span>📱 Mobile Friendly</span>
              <span>🏆 High Scores</span>
              <span>⬆️ 12 Levels</span>
            </div>
            <Link to="/game" className="lp-game__btn">🕹️ Play Now — It's Free!</Link>
          </div>
          <div className="lp-game__right">
            <div className="lp-game__preview">
              <div className="lp-game__preview-screen">
                <div className="lp-game__falling">
                  {['🍛','⭐','🥘','🌶️','🥟','💎'].map((e, i) => (
                    <span key={i} className="lp-game__fall-item" style={{ left: `${10 + i * 14}%`, animationDelay: `${i * 0.4}s`, animationDuration: `${1.8 + i * 0.3}s` }}>{e}</span>
                  ))}
                </div>
                <div className="lp-game__plate-demo">🍽️</div>
                <div className="lp-game__preview-score">⭐ 1,250 pts</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ FINAL CTA ═══════ */}
      <section className="lp-cta lp-reveal" ref={addRef}>
        <div className="lp-cta__inner">
          <h2 className="lp-cta__title">Ready to Order?</h2>
          <p className="lp-cta__desc">
            Explore our full menu or get in touch for catering your next event.
          </p>
          <div className="lp-cta__actions">
            <Link to="/menu" className="lp-hero__btn lp-hero__btn--primary">
              Explore Menu
            </Link>
            <Link to="/Catering" className="lp-hero__btn lp-hero__btn--glass lp-hero__btn--dark">
              Catering Inquiry
            </Link>
            <a href="tel:+14794129916" className="lp-hero__btn lp-hero__btn--outline">
              Call Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
